import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';

import type { TicketQuery } from '../api/endpoints';
import type { TicketPriority, TicketStatus } from '../api/types';
import { ErrorMessage } from '../components/ErrorMessage';
import { Icon } from '../components/icons';
import { PageLoader } from '../components/Loader';
import { BookingClarification } from '../m365/BookingClarification';
import { InquiryPanel } from '../m365/InquiryPanel';
import { deliveryLabels, fmtDateTime } from '../ops/format';
import { extractAction, parseRichText } from '../ops/richTextRules';
import { isMissedUnknownCall } from '../ops/missedCall';
import { MissedCallPanel } from '../ops/MissedCallPanel';
import { RichText } from '../ops/RichText';
import { TicketList } from '../ops/TicketCards';
import { TicketActions, TicketCreateForm, TicketThread } from '../ops/TicketPanel';
import { ago, dueInfo, initials } from '../ops/ticketView';
import { assigneeLabel, isOpen, teamLabels, ticketPriorityLabels, ticketStatusLabels } from '../ops/tickets';
import { useFieldOnly, useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';

// TicketFilters: Projekt ist durch den Kontext gesetzt; Status, Priorität, Kategorie, Verantwortlicher, Fälligkeit.
export function TicketFilters({ value, onChange, manage, fieldOnly = false }: { value: TicketQuery; onChange: (q: TicketQuery) => void; manage: boolean; fieldOnly?: boolean }) {
  const { project } = useProject();
  const papi = useProjectApi();
  const categories = useQuery({ queryKey: projectKeys.ticketCategories(project.id), queryFn: ({ signal }) => papi.ticketCategories(signal), staleTime: 300_000 });
  const status = value.status?.[0] ?? '';
  const active = [value.priority?.[0], value.category, value.role, value.due, status !== 'open' ? 'status' : undefined].filter(Boolean).length;
  const [open, setOpen] = useState(active > 0);
  return (
    <form className="ticket-filters" role="search" aria-label="Rückfragen filtern" onSubmit={(e) => e.preventDefault()}>
      <div className="ticket-filter-bar">
        <label className="field ticket-search">
          <span className="visually-hidden">Suche</span>
          <input type="search" value={value.q ?? ''} onChange={(e) => onChange({ ...value, q: e.target.value || undefined })} placeholder="Betreff suchen …" />
        </label>
        <button type="button" className={open ? 'btn btn-ghost filter-toggle active' : 'btn btn-ghost filter-toggle'} aria-expanded={open} onClick={() => setOpen((v) => !v)}>
          Filter{active > 0 && <span className="filter-count">{active}</span>}
        </button>
      </div>
      {open && (
        <div className="filters ticket-filter-panel">
          <label className="field narrow">
            <span>Status</span>
            <select value={status} onChange={(e) => onChange({ ...value, status: e.target.value ? [e.target.value] : undefined })}>
              <option value="open">Offen (alle)</option>
              {(Object.keys(ticketStatusLabels) as TicketStatus[]).map((s) => (
                <option key={s} value={s}>
                  {ticketStatusLabels[s]}
                </option>
              ))}
              <option value="">Alle</option>
            </select>
          </label>
          <label className="field narrow">
            <span>Priorität</span>
            <select value={value.priority?.[0] ?? ''} onChange={(e) => onChange({ ...value, priority: e.target.value ? [e.target.value] : undefined })}>
              <option value="">Alle</option>
              {(Object.keys(ticketPriorityLabels) as TicketPriority[]).map((p) => (
                <option key={p} value={p}>
                  {ticketPriorityLabels[p]}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Kategorie</span>
            <select value={value.category ?? ''} onChange={(e) => onChange({ ...value, category: e.target.value || undefined })}>
              <option value="">Alle</option>
              {(categories.data?.items ?? []).map((c) => (
                <option key={c.id} value={c.key}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Verantwortlich</span>
            <select value={value.assigned ?? defaultAssigned(manage, fieldOnly)} onChange={(e) => onChange({ ...value, assigned: e.target.value, role: undefined })}>
              {!manage && <option value="involved">Meine (gestellt, zugewiesen, zu meinen Terminen)</option>}
              <option value="mine">Ich oder mein Team</option>
              <option value="me">Nur ich</option>
              <option value="team">Mein Team</option>
              {manage && <option value="unassigned">Nicht zugewiesen</option>}
              {manage && <option value="all">Alle</option>}
            </select>
          </label>
          {manage && (
            <label className="field narrow">
              <span>Team</span>
              <select value={value.role ?? ''} onChange={(e) => onChange({ ...value, role: e.target.value || undefined, assigned: e.target.value ? 'all' : value.assigned })}>
                <option value="">Alle</option>
                {Object.entries(teamLabels).map(([k, l]) => (
                  <option key={k} value={k}>
                    {l}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="field narrow">
            <span>Fälligkeit</span>
            <select value={value.due ?? ''} onChange={(e) => onChange({ ...value, due: (e.target.value || undefined) as TicketQuery['due'] })}>
              <option value="">Alle</option>
              <option value="overdue">Überfällig</option>
              <option value="today">Bis heute</option>
              <option value="week">Diese Woche</option>
            </select>
          </label>
        </div>
      )}
    </form>
  );
}

// Makler sehen standardmäßig nur, woran sie beteiligt sind – nicht alle Rückfragen des Makler-Teams (B-28).
const defaultAssigned = (manage: boolean, fieldOnly: boolean) => (manage ? 'all' : fieldOnly ? 'involved' : 'mine');

const fromParams = (params: URLSearchParams, manage: boolean, fieldOnly: boolean): TicketQuery => ({
  status: params.get('status') === 'all' ? undefined : [params.get('status') ?? 'open'],
  priority: params.get('priority') ? [params.get('priority') as string] : undefined,
  category: params.get('category') ?? undefined,
  assigned: params.get('assigned') ?? defaultAssigned(manage, fieldOnly),
  role: params.get('role') ?? undefined,
  due: (params.get('due') as TicketQuery['due']) ?? undefined,
  q: params.get('q') ?? undefined,
  sort: 'priority',
});

// TicketsPage: „Rückfragen & Aufgaben“ – eigene und Team-Vorgänge; Verantwortliche sehen alle.
export function TicketsPage() {
  const { project, can } = useProject();
  const queryClient = useQueryClient();
  const manage = can('ticket.manage');
  const [params, setParams] = useSearchParams();
  const fieldOnly = useFieldOnly();
  const q = fromParams(params, manage, fieldOnly);
  const [creating, setCreating] = useState<'' | 'REQUEST' | 'TASK'>('');
  const apply = (next: TicketQuery) => {
    const p = new URLSearchParams();
    const st = next.status?.[0];
    p.set('status', st ?? 'all');
    if (next.priority?.[0]) p.set('priority', next.priority[0]);
    if (next.category) p.set('category', next.category);
    if (next.assigned) p.set('assigned', next.assigned);
    if (next.role) p.set('role', next.role);
    if (next.due) p.set('due', next.due);
    if (next.q) p.set('q', next.q);
    setParams(p, { replace: true });
  };
  return (
    <div className="page">
      <header className="page-header">
        <h1>Rückfragen & Aufgaben</h1>
        {can('ticket.create') && (
          <div className="form-actions">
            <button type="button" className="btn btn-primary" onClick={() => setCreating('REQUEST')}>
              Neue Rückfrage
            </button>
            <button type="button" className="btn" onClick={() => setCreating('TASK')}>
              Neue Aufgabe
            </button>
          </div>
        )}
      </header>
      {creating && (
        <TicketCreateForm
          kind={creating}
          context={{}}
          onCancel={() => setCreating('')}
          onDone={() => {
            setCreating('');
            void queryClient.invalidateQueries({ queryKey: projectKeys.all(project.id) });
          }}
        />
      )}
      <TicketFilters value={q} onChange={apply} manage={manage} fieldOnly={fieldOnly} />
      <TicketList q={q} />
    </div>
  );
}

// TicketDetailPage: ein Vorgang auf einen Blick – oben was es ist, wer zuständig ist und bis wann, dazu die
// Arbeitsschritte; darunter „Was ist zu tun?“, die Einzelheiten, die Unterhaltung und rechts der Bezug.
export function TicketDetailPage() {
  const { ticketId = '' } = useParams();
  const { project } = useProject();
  const papi = useProjectApi();
  const detail = useQuery({ queryKey: projectKeys.ticket(project.id, ticketId), queryFn: ({ signal }) => papi.ticket(ticketId, signal) });
  if (detail.isPending) return <PageLoader label="Rückfrage wird geladen …" />;
  if (detail.isError) return <ErrorMessage error={detail.error} onRetry={() => void detail.refetch()} />;
  const t = detail.data;
  const due = dueInfo(t);
  // „Was ist zu tun?“ steht oben, solange der Vorgang offen ist; danach als normaler Satz bei den Einzelheiten.
  const blocks = parseRichText(t.description);
  const { action, rest } = isOpen(t.status)
    ? extractAction(blocks)
    : { action: null, rest: blocks.map((b) => (b.kind === 'action' ? { kind: 'paragraph' as const, lines: b.lines, leadIn: false } : b)) };
  const base = `/p/${project.key}`;
  const side = Boolean(t.lead_id || t.appointment_id || t.emails.length > 0);
  return (
    <div className="page ticket-detail">
      <Link className="back-link" to={`${base}/posteingang`}>
        ← Posteingang
      </Link>
      <header className={`ticket-hero prio-${t.priority.toLowerCase()}`}>
        <div className="ticket-hero-main">
          <p className="ticket-kicker">
            <span className="kind-chip">
              <Icon name={t.kind === 'TASK' ? 'check' : 'message'} size={14} />
              {t.kind === 'TASK' ? 'Aufgabe' : 'Rückfrage'}
            </span>
            <span className="tag-chip">{t.category_name ?? t.category}</span>
            <span className="muted">{t.reference}</span>
          </p>
          <h1>{t.subject}</h1>
          <ul className="ticket-facts" aria-label="Eckdaten">
            <li>
              <span className="label">Status</span>
              <span className={isOpen(t.status) ? 'badge badge-info' : 'badge badge-muted'}>{ticketStatusLabels[t.status]}</span>
            </li>
            {t.priority !== 'NORMAL' && (
              <li>
                <span className="label">Priorität</span>
                <span className={t.priority === 'URGENT' ? 'badge badge-error' : t.priority === 'HIGH' ? 'badge badge-warn' : 'badge badge-muted'}>
                  {ticketPriorityLabels[t.priority]}
                </span>
              </li>
            )}
            <li>
              <span className="label">Fällig</span>
              {due ? (
                <span className={`due due-${due.tone}`} title={fmtDateTime(t.due_at)}>
                  <Icon name="clock" size={14} />
                  {due.label}
                </span>
              ) : (
                <span className="muted">ohne Fälligkeit</span>
              )}
            </li>
            <li>
              <span className="label">Zuständig</span>
              <span className="assignee">
                <span className={t.assigned_to_name ? 'avatar' : 'avatar team'} aria-hidden="true">
                  {t.assigned_to_name ? initials(t.assigned_to_name) : <Icon name="user" size={12} />}
                </span>
                {assigneeLabel(t)}
              </span>
            </li>
            <li>
              <span className="label">Erstellt</span>
              <span title={fmtDateTime(t.created_at)}>
                {t.created_by_name ?? 'System'} · {ago(t.created_at)}
              </span>
            </li>
          </ul>
        </div>
        <TicketActions t={t} />
      </header>
      {action && (
        <section className="next-step" aria-label="Was ist zu tun?">
          <span className="next-step-icon" aria-hidden="true">
            <Icon name="check" size={18} />
          </span>
          <div>
            <h2>Was ist zu tun?</h2>
            <RichText blocks={[{ kind: 'paragraph', lines: action.lines, leadIn: false }]} />
          </div>
        </section>
      )}
      {isMissedUnknownCall(t) && <MissedCallPanel ticket={t} />}
      <div className={side ? 'ticket-columns' : 'ticket-columns single'}>
        <div className="ticket-main-col">
          {rest.length > 0 && (
            <section className="card">
              <h2>Einzelheiten</h2>
              <RichText blocks={rest} />
            </section>
          )}
          {t.category === 'BOOKING_CLARIFICATION' && <BookingClarification ticketId={t.id} />}
          {(t.category === 'PROPERTY_INQUIRY' || t.category === 'INQUIRY_CLARIFICATION') && <InquiryPanel ticketId={t.id} />}
          <section className="card">
            <h2>Unterhaltung</h2>
            <TicketThread ticketId={t.id} showHistory withDescription={false} withActions={false} />
          </section>
        </div>
        {side && (
          <aside className="ticket-side-col">
            {(t.lead_id || t.appointment_id) && (
              <section className="card">
                <h2>Bezug</h2>
                <ul className="relation-list">
                  {t.lead_id && (
                    <li>
                      <Icon name="user" size={16} />
                      <span>
                        <span className="label">Lead</span>
                        <Link to={`${base}/leads/${t.lead_id}`}>{t.lead_name ?? 'Lead öffnen'}</Link>
                      </span>
                    </li>
                  )}
                  {t.appointment_id && (
                    <li>
                      <Icon name="calendar" size={16} />
                      <span>
                        <span className="label">Termin</span>
                        <Link to={`${base}/termine/${t.appointment_id}`}>{fmtDateTime(t.appointment_start_at)}</Link>
                      </span>
                    </li>
                  )}
                </ul>
              </section>
            )}
            {t.emails.length > 0 && (
              <section className="card">
                <h2>E-Mails</h2>
                <ul className="mail-list">
                  {t.emails.map((m) => (
                    <li key={m.id}>
                      <span className="mail-direction" aria-hidden="true">
                        <Icon name="mail" size={14} />
                      </span>
                      <span>
                        <strong>{m.subject}</strong>
                        <span className="muted block">
                          {m.direction === 'INBOUND' ? 'von' : 'an'} {m.sender_address ?? ''} · {ago(m.received_at ?? m.sent_at ?? m.created_at)}
                          {m.delivery_status && (
                            <>
                              {' '}
                              <span className={`badge ${deliveryLabels[m.delivery_status]?.badge ?? 'badge-muted'}`}>
                                {deliveryLabels[m.delivery_status]?.text ?? m.delivery_status}
                              </span>
                            </>
                          )}
                        </span>
                        {m.body_preview && <span className="mail-preview">{m.body_preview}</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </aside>
        )}
      </div>
    </div>
  );
}
